import React, { useCallback, useState } from "react";
import { Col, Divider, Form, Modal, Row, Select, Space, Spin } from "antd";
import { makeStyles } from "@material-ui/core/styles";
import CustomButton from "components/Common/buttons/CustomButton";
import NGIForm from "components/Common/form/NGIForm";
import Title from "antd/lib/typography/Title";
import classnames from "classnames";
import NGIFormItem from "components/Common/form/NGIFormItem";
import ConfirmationModal from "components/Common/modals/ConfirmationModal";
import { ADD_REMOVE_TYPES, VALIDATION_RULES } from "pages/price-management/CustomerBlacklist/variables";
import {
  downloadFileBlobCsv,
  getCustomerBulkUploadReport,
  retrievePricesDownloadUrlByType,
  startCustomerBulkUploadProcess,
  uploadDataToS3BucketByUrl,
} from "utils/api";
import { defaultNgiErrorMethod, errorMessage, warnMessage } from "components/Common/responses/message";
import csvIcon from "assets/images/svg/CSV.svg";
import { useSelector } from "react-redux";
import { DictionariesSelector } from "store/dictionaries/selectors";
import NGISelect from "components/Common/inputs/NGISelect";
import closeIcon from "assets/images/svg/closeSmall.svg";
import Dragger from "antd/lib/upload/Dragger";
import uploadArea from "assets/images/svg/uploadArea.svg";
import * as xlsx from "xlsx";
import CustomLink from "components/Common/buttons/CustomLink";
import failedIcon from "assets/images/svg/failed-btn.svg";
import headerIcon from "assets/images/svg/bulk-blacklist-customers.svg";
import NGIInput from "components/Common/inputs/NGIInput";
import { generateCSV } from "utils/helpers/functions";
import { CSV_TEMPLATES } from "utils/constants";
const { Option } = Select;

const useStyles = makeStyles({
  modal: {
    maxWidth: "900px!important",
  },
  infoContainer: {
    display: "flex",
    flexDirection: "column",
    textAlign: "left",
    fontWeight: 600,

    "&>span:first-of-type": {
      fontSize: 13,
      fontWeight: 300,
    },
  },
  modalResult: {
    maxWidth: "500px!important",
  },
  formItem: {
    display: "flex",

    "& .ant-col-12 + .ant-col-12": {
      paddingLeft: 10,
    },
  },
  disabled: {
    pointerEvents: "none",
    opacity: 0.6,
  },
  fileContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderRadius: 5,
    border: "1px solid #17C34733",
    padding: 10,
    marginTop: 10,
  },
  removeBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: 32,
    width: 32,
    borderRadius: 50,
    background: "rgba(255, 255, 255, 1)",

    "&:hover": {
      cursor: "pointer",
      opacity: 0.6,
    },
  },
  fileTitle: {
    fontWeight: 600,
    fontSize: 14,

    "&>img": {
      marginRight: 10,
    },
  },
  customerPreview: {
    display: "flex",
    padding: 10,
    border: "1px solid #FF00004D",
    borderRadius: 5,
    alignItems: "center",
  },
  imgContainer: {
    width: 36,
    height: 36,
    marginRight: 15,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#6666661A",
    borderRadius: 3,
    overflow: "hidden",
  },
  imgFound: {
    width: 36,
  },
  found: {
    borderColor: "#0000000F",
  },
  customerName: {
    fontWeight: 600,
    lineHeight: "15px",
  },
  customerRole: {
    color: "#8C8C8C",
    fontWeight: 300,
    fontSize: 11,
  },
  successText: {
    fontWeight: 700,
    color: "#17C347",
  },
  failedText: {
    fontWeight: 700,
    color: "#FF0000",
  },
  text: {
    marginBottom: 30,
    color: "#666666",
    fontSize: 13,
  },
  result: {
    borderRadius: 5,
    padding: 10,
    marginBottom: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  failedResult: {
    border: "1px solid #FF00004D",
  },
  titleContainer: {
    display: "flex",
    alignItems: "center",

    "&>img": {
      marginRight: 10,
    },
  },
  header: {
    display: "flex",
    alignItems: "center",
    borderBottom: "1px solid #00000014",
    paddingBottom: 20,

    "&>img": {
      marginRight: 10,
    },
  },
  downloadTitleContainer: {
    display: "flex",
    justifyContent: "space-between",
  },
});

const AddBulkCustomerModal = ({ type, onClose, update }) => {
  const classes = useStyles();
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedData, setUploadedData] = useState("");
  const [results, setResults] = useState(null);

  const {
    list: { blacklistAddingReason, blacklistRemovingReason },
  } = useSelector(DictionariesSelector);

  const onSave = useCallback(
    values => {
      setIsLoading(true);
      startCustomerBulkUploadProcess({
        operationType: type,
        reason: values.reason,
        comments: values.comments,
      })
        .then(async res => {
          const {
            data: {
              result: { id, url },
            },
          } = res;
          if (!url) {
            warnMessage("URL was not generated");
          }
          return { url, id };
        })
        .then(({ url, id }) => ({ result: uploadDataToS3BucketByUrl(url, uploadedData), id }))
        .then(async ({ id }) => {
          await onRetrieveBulkProcessResults(id);
        })
        .catch(err => {
          setIsLoading(false);
          defaultNgiErrorMethod(err);
        });
    },
    [uploadedData, type]
  );

  const onRetrieveBulkProcessResults = useCallback(async id => {
    setTimeout(async () => {
      await getCustomerBulkUploadReport(id).then(async res => {
        if (res?.data?.result?.isCompleted) {
          setResults(res?.data?.result);
          setIsLoading(false);
        } else {
          await onRetrieveBulkProcessResults(id);
        }
      });
    }, 3000);
  }, []);

  const openConfirmationModal = useCallback(() => setIsConfirmationOpen(true), []);
  const closeConfirmationModal = useCallback(() => setIsConfirmationOpen(false), []);

  const removeFile = useCallback(() => setUploadedFile(null), []);

  const readUploadFile = useCallback(e => {
    setIsLoading(true);
    const file = e.file;

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = e => {
        const data = e.target.result;
        const workbook = xlsx.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const json = xlsx.utils.sheet_to_json(worksheet, { raw: false });
        const csv = xlsx.utils.sheet_to_csv(worksheet);
        const formattedResult = json.map(row => ({ ...row }));
        const noError = formattedResult.every(item => item?.customerId || null);

        if (!noError) {
          errorMessage("Please upload the correct ‘*.csv’ file");
          setIsLoading(false);
          return;
        }
        if (noError) {
          setUploadedData(csv);
          setUploadedFile(file);
        } else {
          setUploadedData("");
          setUploadedFile(null);
        }
        setIsLoading(false);
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  }, []);

  const onReportDownload = useCallback(fileName => {
    setIsLoading(true);
    retrievePricesDownloadUrlByType("BlacklistBulkFailedReport", fileName)
      .then(res => {
        const {
          result: { url },
        } = res.data;

        downloadFileBlobCsv(url)
          .then(blob => {
            const url = window.URL.createObjectURL(new Blob([blob.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `${fileName}`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
          })
          .catch(e => {
            defaultNgiErrorMethod(e);
          });
        setIsLoading(false);
      })
      .catch(e => {
        defaultNgiErrorMethod(e);
        setIsLoading(false);
      });
  }, []);

  const handleOnClose = useCallback(() => {
    update();
    onClose();
  }, []);

  return (
    <Modal
      title={
        <div className={classes.header}>
          {!results && <img src={headerIcon} alt="" />}
          <div className={classes.infoContainer}>
            {type === ADD_REMOVE_TYPES.ADD ? "Bulk Blacklist Customers" : "Bulk Remove from Blacklist"}
            {!results && <span>Add Customers via *.CSV upload</span>}
          </div>
        </div>
      }
      className={results ? classes.modalResult : classes.modal}
      open
      onCancel={onClose}
      closable={false}
      footer={[]}
    >
      {!results ? (
        <Spin spinning={isLoading}>
          <NGIForm name="bulkCustomer" form={form} onFinish={onSave}>
            <Row className={classes.formItem}>
              <Col span={24}>
                <Title level={5}>Reason for {type === ADD_REMOVE_TYPES.ADD ? "Adding" : "Removing"}*</Title>
                <NGIFormItem name="reason" rules={VALIDATION_RULES}>
                  <NGISelect showSearch placeholder="Select value">
                    {(type === ADD_REMOVE_TYPES.ADD ? blacklistAddingReason : blacklistRemovingReason)?.map(item => (
                      <Option key={item} value={item}>
                        {item}
                      </Option>
                    ))}
                  </NGISelect>
                </NGIFormItem>
              </Col>
            </Row>
            <Row className={classes.formItem}>
              <Col span={24}>
                <Title level={5}>Additional Comments</Title>
                <NGIFormItem name="comments">
                  <NGIInput placeholder="Additional Comments" />
                </NGIFormItem>
              </Col>
            </Row>
            <Row className={classes.formItem}>
              <Col span={24}>
                <div className={classes.downloadTitleContainer}>
                  <Title level={5}>Customers List *.CSV File*</Title>
                  <CustomLink color="#0085FF" onClick={() => generateCSV(CSV_TEMPLATES.ADD_CUSTOMERS_BLACKLIST)}>
                    Download *.csv template
                  </CustomLink>
                </div>
                <NGIFormItem name="file" rules={[{ required: true, message: "Please upload file" }]}>
                  {uploadedFile ? (
                    <div className={classes.fileContainer}>
                      <div className={classes.fileTitle}>
                        <img src={csvIcon} alt="" />
                        {`${uploadedFile.name}`}
                      </div>
                      <div onClick={removeFile} className={classes.removeBtn}>
                        <img src={closeIcon} alt="remove" />
                      </div>
                    </div>
                  ) : (
                    <Dragger accept=".csv,.xls" showUploadList={false} customRequest={readUploadFile} name="file">
                      <img className="upload-bg" src={uploadArea} alt="upload" />
                      <p className="ant-upload-text">Click or drag file to this area to upload</p>
                      <p className="ant-upload-hint">
                        Support for a single upload. Strictly prohibit from uploading company data or other band files
                      </p>
                    </Dragger>
                  )}
                </NGIFormItem>
              </Col>
            </Row>
          </NGIForm>
          <Divider />
          <br />
          <Space>
            <CustomButton onClick={onClose} type="primary" size="small" color="outlined" text="Cancel" />
            <CustomButton
              disabled={!uploadedFile}
              type="primary"
              onClick={openConfirmationModal}
              text="CONFIRM"
              size="small"
            />
          </Space>
        </Spin>
      ) : (
        <div>
          <div className={classes.text}>
            A total of <span className={classes.successText}>{results.successItemsCount}</span> customers were
            successfully
            {type === ADD_REMOVE_TYPES.ADD ? " added to blacklist" : " removed from the blacklist"}, while{" "}
            <span className={classes.failedText}>{results.failedItemsCount}</span> could not be processed. You can
            download the list of customers below for further review.
          </div>

          <div className={classnames(classes.result, classes.failedResult)}>
            <div className={classes.titleContainer}>
              <img src={failedIcon} alt="" />
              <b>{results.failedItemsCount} Customers</b>
            </div>
            {!!results.failedItemsCount && (
              <CustomLink
                color="#0085FF"
                onClick={() => {
                  onReportDownload(results.failedItemsReportUrl);
                }}
              >
                Download *.CSV
              </CustomLink>
            )}
          </div>

          <Divider />
          <Space>
            <CustomButton color="outlined" onClick={handleOnClose} text="Close" size="small" />
          </Space>
        </div>
      )}

      {isConfirmationOpen && (
        <ConfirmationModal
          description={`Are you sure you want to ${type === ADD_REMOVE_TYPES.ADD ? "add" : "remove"} customers ${
            type === ADD_REMOVE_TYPES.ADD ? "to" : "from"
          } the blacklist?`}
          onCancel={closeConfirmationModal}
          onConfirm={form.submit}
          loading={isLoading}
        />
      )}
    </Modal>
  );
};

export default AddBulkCustomerModal;
